/**
 * Checkbox ПРРО Service
 * Frontend client for Vercel API routes → Checkbox API
 */
import { apiFetch } from './api';
import * as paylink from './paylinkService';

const API_BASE = '/api/checkbox';

let cachedToken = null;
let cachedCredentials = null; // { license_key, pin_code }
let defaultTaxCodes = null;   // e.g. [1] for ПДВ 20%

/** Set credentials for auto-relogin */
export function setCredentials(license_key, pin_code) {
  cachedCredentials = { license_key, pin_code };
  cachedToken = null;
}

/** Set pre-obtained token and license key (from server-side init) */
export function setToken(token, license_key) {
  cachedToken = token;
  if (!cachedCredentials) cachedCredentials = { license_key, pin_code: '' };
}

/** Set default tax codes for all receipts */
export function setDefaultTaxCodes(codes) {
  defaultTaxCodes = Array.isArray(codes) && codes.length ? codes : null;
}

/** Get current default tax codes */
export function getDefaultTaxCodes() {
  return defaultTaxCodes;
}

/** Clear session */
export function clearSession() {
  cachedToken = null;
  cachedCredentials = null;
  defaultTaxCodes = null;
}

/**
 * Test connection using credentials stored in Firestore. PIN never leaves
 * the backend — avoids the redacted-placeholder bug where the GET-side
 * '••••••' could be shipped as the real PIN.
 * Returns { ok, access_token?, license_key?, shift?, error?, detail? }.
 */
export async function testStoredConnection(locationId) {
  const qs = locationId != null ? `&locationId=${encodeURIComponent(locationId)}` : '';
  return apiFetch(`${API_BASE}?action=test${qs}`, { method: 'POST', body: {} });
}

/** Signin and get JWT token */
export async function signin(license_key, pin_code) {
  const data = await apiFetch(`${API_BASE}?action=signin`, {
    method: 'POST',
    body: { pin_code, license_key },
  });
  cachedToken = data.access_token;
  cachedCredentials = { license_key, pin_code };
  return data;
}

/** Auto-signin if token expired */
async function ensureToken() {
  if (cachedToken) return cachedToken;
  if (!cachedCredentials) throw new Error('Checkbox не налаштований. Введіть ключ ліцензії та PIN-код у налаштуваннях.');
  await signin(cachedCredentials.license_key, cachedCredentials.pin_code);
  return cachedToken;
}

/** Make authenticated request with auto-retry on 401 */
async function authFetch(url, options = {}) {
  const token = await ensureToken();
  const extraHeaders = {
    'X-Checkbox-Token': token,
    'X-License-Key': cachedCredentials.license_key,
  };

  try {
    return await apiFetch(url, {
      ...options,
      headers: { ...options.headers, ...extraHeaders },
    });
  } catch (err) {
    // Retry once on 401 (token expired)
    if (err.message.includes('401')) {
      cachedToken = null;
      const newToken = await ensureToken();
      extraHeaders['X-Checkbox-Token'] = newToken;
      return apiFetch(url, {
        ...options,
        headers: { ...options.headers, ...extraHeaders },
      });
    }
    throw err;
  }
}

/** Open a new shift */
export async function openShift() {
  return authFetch(`${API_BASE}?action=shift-open`, { method: 'POST', body: {} });
}

/** Close current shift (generates Z-report) */
export async function closeShift() {
  return authFetch(`${API_BASE}?action=shift-close`, { method: 'POST', body: {} });
}

/** Get available tax rates for current cashier */
export async function getTaxRates() {
  return authFetch(`${API_BASE}?action=tax`);
}

/** Get current shift status */
export async function getShiftStatus() {
  try {
    return await authFetch(`${API_BASE}?action=shift-status`);
  } catch {
    return null; // No active shift
  }
}

/** Check if fiscal receipt is required (configured + shift open) */
export async function isFiscalRequired() {
  if (!isConfigured()) return false;
  try {
    const shift = await getShiftStatus();
    return shift?.status === 'OPENED';
  } catch {
    return false;
  }
}

/**
 * Create a fiscal receipt (single item)
 * @param {string} serviceName - Service name
 * @param {number} amount - Amount in UAH (e.g. 720.50)
 * @param {string} method - Payment method: 'cash' | 'terminal' | 'transfer' | 'online'
 * @param {string} [code] - Product/service code (defaults to 'S-1')
 */
export async function createReceipt(serviceName, amount, method, code = 'S-1') {
  const priceInKopecks = Math.round(amount * 100);
  const paymentType = method === 'cash' ? 'CASH' : 'CASHLESS';

  const good = {
    code: String(code),
    name: serviceName,
    price: priceInKopecks,
  };
  if (defaultTaxCodes) good.tax = defaultTaxCodes;

  const body = {
    goods: [{
      good,
      quantity: 1000,
      is_return: false,
    }],
    payments: [{
      type: paymentType,
      value: priceInKopecks,
    }],
  };

  return authFetch(`${API_BASE}?action=receipt`, {
    method: 'POST',
    body,
  });
}

/**
 * Create a fiscal receipt with multiple items
 * @param {Array<{code: string|number, name: string, price: number, quantity: number}>} items - Items (price in UAH, quantity in units)
 * @param {number} totalAmount - Total amount in UAH
 * @param {string} method - Payment method: 'cash' | 'terminal' | 'transfer' | 'online'
 */
export async function createReceiptMulti(items, totalAmount, method) {
  const paymentType = method === 'cash' ? 'CASH' : 'CASHLESS';

  const body = {
    goods: items.map(item => {
      const good = {
        code: String(item.code),
        name: item.name,
        price: Math.round(item.price * 100),
      };
      if (defaultTaxCodes) good.tax = defaultTaxCodes;
      return { good, quantity: Math.round(item.quantity * 1000), is_return: false };
    }),
    payments: [{
      type: paymentType,
      value: Math.round(totalAmount * 100),
    }],
  };

  return authFetch(`${API_BASE}?action=receipt`, {
    method: 'POST',
    body,
  });
}

/**
 * Create a fiscal RETURN receipt (single item)
 * @param {string} serviceName - Service name
 * @param {number} amount - Amount in UAH (positive value)
 * @param {string} method - Original payment method: 'cash' | 'terminal' | 'transfer' | 'online'
 * @param {string} [code] - Product/service code (defaults to 'S-1')
 */
export async function createReturnReceipt(serviceName, amount, method, code = 'S-1') {
  const priceInKopecks = Math.round(amount * 100);
  const paymentType = method === 'cash' ? 'CASH' : 'CASHLESS';

  const good = {
    code: String(code),
    name: serviceName,
    price: priceInKopecks,
  };
  if (defaultTaxCodes) good.tax = defaultTaxCodes;

  const body = {
    goods: [{
      good,
      quantity: 1000,
      is_return: true,
    }],
    payments: [{
      type: paymentType,
      value: priceInKopecks,
    }],
  };

  return authFetch(`${API_BASE}?action=receipt`, {
    method: 'POST',
    body,
  });
}

/**
 * Create a fiscal RETURN receipt with multiple items
 * @param {Array<{code: string|number, name: string, price: number, quantity: number}>} items - Items (price in UAH, quantity in units)
 * @param {number} totalAmount - Total amount in UAH (positive value)
 * @param {string} method - Original payment method
 */
export async function createReturnReceiptMulti(items, totalAmount, method) {
  const paymentType = method === 'cash' ? 'CASH' : 'CASHLESS';

  const body = {
    goods: items.map(item => {
      const good = {
        code: String(item.code),
        name: item.name,
        price: Math.round(item.price * 100),
      };
      if (defaultTaxCodes) good.tax = defaultTaxCodes;
      return { good, quantity: Math.round(item.quantity * 1000), is_return: true };
    }),
    payments: [{
      type: paymentType,
      value: Math.round(totalAmount * 100),
    }],
  };

  return authFetch(`${API_BASE}?action=receipt`, {
    method: 'POST',
    body,
  });
}

/** Check if Checkbox is configured */
export function isConfigured() {
  return !!(cachedCredentials && cachedCredentials.license_key && cachedCredentials.pin_code);
}

/**
 * Process terminal payment via PayLink (if configured) before fiscal receipt.
 * Call this BEFORE createReceipt/createReceiptMulti when method === 'terminal'.
 * @param {number} amount - Amount in UAH
 * @returns {Promise<Object|null>} Terminal data (card_mask, rrn, auth_code...) or null if PayLink not configured
 */
export async function processTerminalPayment(amount) {
  if (!paylink.isConfigured()) return null;
  const amountKopecks = Math.round(amount * 100);
  return paylink.purchase(amountKopecks);
}
